<template>
    <div class="chats-page">
        <CCard>
            <CCardHeader class="d-flex justify-content-between align-items-center">
                <strong>Чаты</strong>
                <div class="d-flex align-items-center">
                    <CLink to="/settings/chat-quick-replies" class="mr-3">Шаблоны сообщений</CLink>
                    <small class="text-muted">Обновление каждые 15 секунд</small>
                </div>
            </CCardHeader>
            <CCardBody>
                <CForm class="chats-filters mb-3">
                    <CRow>
                        <CCol cols="12" md="3">
                            <CSelect
                                class="mb-2"
                                label="Статус"
                                horizontal
                                :value.sync="statusFilter"
                                :options="statusOptions"
                            />
                        </CCol>
                        <CCol cols="12" md="3">
                            <CSelect
                                class="mb-2"
                                label="Агент"
                                horizontal
                                :value.sync="adminFilter"
                                :options="adminOptions"
                            />
                        </CCol>
                        <CCol cols="12" md="6">
                            <CInput
                                v-model="searchInput"
                                class="mb-2"
                                label="Поиск"
                                horizontal
                                placeholder="Ключевое слово в переписке..."
                                @input="onSearchInput"
                            />
                        </CCol>
                    </CRow>
                </CForm>

                <div v-if="loading" class="table__preloader">
                    <CSpinner color="primary" />
                </div>
                <template v-else>
                    <div v-if="!chats.length" class="text-muted">
                        {{ emptyMessage }}
                    </div>
                    <div v-else class="chat-summary-cards">
                        <ChatSummaryCard
                            v-for="chat in chats"
                            :key="chat.chat_id"
                            :chat="chat"
                            @select="openChat"
                        />
                    </div>
                    <CPagination
                        v-if="pages > 1"
                        :active-page.sync="activePage"
                        class="mt-4"
                        :pages="pages"
                        align="center"
                    />
                </template>
            </CCardBody>
        </CCard>
    </div>
</template>

<script>
    import Vue from 'vue';
    import ChatSummaryCard from '@/components/chats/ChatSummaryCard';

    const PAGE_SIZE = 10;
    const SEARCH_DEBOUNCE_MS = 350;

    export default {
        name: 'Chats',
        components: {
            ChatSummaryCard
        },
        data() {
            return {
                loading: true,
                chats: [],
                total: 0,
                refreshTimerId: null,
                searchInput: '',
                searchDebounceId: null,
                adminOptions: [{ value: '', label: 'Все' }],
                statusOptions: [
                    { value: 'all', label: 'Все' },
                    { value: 'open', label: 'Открытые' },
                    { value: 'closed', label: 'Закрытые' }
                ]
            };
        },
        computed: {
            activePage: {
                get() {
                    return parseInt(this.$route.query.page, 10) || 1;
                },
                set(page) {
                    this.pushFilters({ page });
                }
            },
            statusFilter: {
                get() {
                    const status = String(this.$route.query.status || 'all');
                    return ['open', 'closed', 'all'].includes(status) ? status : 'all';
                },
                set(status) {
                    this.pushFilters({
                        status: status === 'all' ? undefined : status,
                        page: 1
                    });
                }
            },
            adminFilter: {
                get() {
                    return this.$route.query.admin_id ? String(this.$route.query.admin_id) : '';
                },
                set(adminId) {
                    this.pushFilters({
                        admin_id: adminId || undefined,
                        page: 1
                    });
                }
            },
            searchQuery() {
                return String(this.$route.query.q || '').trim();
            },
            hasActiveFilters() {
                return this.statusFilter !== 'all'
                    || Boolean(this.adminFilter)
                    || Boolean(this.searchQuery);
            },
            emptyMessage() {
                return this.hasActiveFilters
                    ? 'Чаты не найдены'
                    : 'Активных чатов пока нет';
            },
            pages() {
                return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
            }
        },
        watch: {
            '$route.query': {
                deep: true,
                handler() {
                    this.searchInput = this.searchQuery;
                    this.loadChats();
                }
            }
        },
        async mounted() {
            this.searchInput = this.searchQuery;
            await Promise.all([
                this.loadAdministrators(),
                this.loadChats()
            ]);
            this.refreshTimerId = setInterval(() => {
                this.loadChats(false);
            }, 15000);
        },
        beforeDestroy() {
            if (this.refreshTimerId) {
                clearInterval(this.refreshTimerId);
                this.refreshTimerId = null;
            }
            if (this.searchDebounceId) {
                clearTimeout(this.searchDebounceId);
                this.searchDebounceId = null;
            }
        },
        methods: {
            pushFilters(patch = {}) {
                const nextQuery = {
                    ...this.$route.query,
                    ...patch
                };

                Object.keys(nextQuery).forEach(key => {
                    if (nextQuery[key] === undefined || nextQuery[key] === null || nextQuery[key] === '') {
                        delete nextQuery[key];
                    }
                });

                if (Number(nextQuery.page) === 1) {
                    delete nextQuery.page;
                }

                const current = this.$route.query;
                const keys = new Set([
                    ...Object.keys(current),
                    ...Object.keys(nextQuery)
                ]);
                const changed = [...keys].some(key => String(current[key] || '') !== String(nextQuery[key] || ''));
                if (!changed) {
                    return;
                }

                this.$router.push({ query: nextQuery });
            },
            onSearchInput() {
                if (this.searchDebounceId) {
                    clearTimeout(this.searchDebounceId);
                }
                this.searchDebounceId = setTimeout(() => {
                    const q = String(this.searchInput || '').trim();
                    this.pushFilters({
                        q: q || undefined,
                        page: 1
                    });
                }, SEARCH_DEBOUNCE_MS);
            },
            async loadAdministrators() {
                try {
                    const { data } = await Vue.axios.get('/administrators');
                    const options = [{ value: '', label: 'Все' }];
                    (data || []).forEach(admin => {
                        options.push({
                            value: String(admin.id),
                            label: admin.name || admin.email || `Админ #${admin.id}`
                        });
                    });
                    this.adminOptions = options;
                } catch (e) {
                    this.$root.$emit('showError', 'Не удалось загрузить список агентов');
                }
            },
            async loadChats(useLoader = true) {
                if (useLoader) {
                    this.loading = true;
                }
                try {
                    const params = {
                        page: this.activePage,
                        limit: PAGE_SIZE,
                        status: this.statusFilter
                    };
                    if (this.adminFilter) {
                        params.admin_id = this.adminFilter;
                    }
                    if (this.searchQuery) {
                        params.q = this.searchQuery;
                    }

                    const { data } = await Vue.axios.get('/chats/active', { params });
                    this.chats = data.items || [];
                    this.total = Number(data.total) || 0;
                } catch (e) {
                    this.$root.$emit('showError', 'Не удалось загрузить активные чаты');
                } finally {
                    this.loading = false;
                }
            },
            openChat(chat) {
                this.$router.push({
                    name: 'Chat',
                    params: {
                        chatId: chat.chat_id
                    }
                });
            }
        }
    };
</script>
